import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmtDay } from '@/lib/dates';
import { CalendarClock, AlertTriangle, ShieldCheck, Users, XCircle } from 'lucide-react';

const fmt = (d) => fmtDay(d, { day: 'numeric', month: 'short', year: 'numeric' });
const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'active', label: 'Active', test: r => r.status === 'active' },
  { key: 'expiring', label: 'Expiring ≤ 7 days', test: r => r.expiring_soon },
  { key: 'approval', label: 'Awaiting approval', test: r => r.awaiting_approval },
  { key: 'ended', label: 'Ended', test: r => r.status !== 'active' },
];

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="regen-card p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}1a` }}><Icon className="w-4 h-4" style={{ color }} /></div>
      <div><div className="text-xl font-display font-bold">{value}</div><div className="text-[11px] text-muted-foreground">{label}</div></div>
    </div>
  );
}

function StatusPill({ r }) {
  const [txt, c] = r.status !== 'active' ? ['Ended', '#64748B'] : r.expiring_soon ? [`Ends in ${r.days_left}d`, '#FFB800'] : ['Active', '#22C55E'];
  return <span className="pill text-[11px]" style={{ background: `${c}22`, color: c }} data-testid={`sub-status-${r.id}`}>{txt}</span>;
}

function Row({ r }) {
  return (
    <Link to={`/admin/clients/${r.id}`} data-testid={`sub-row-${r.id}`}
      className="regen-card p-4 grid grid-cols-2 lg:grid-cols-7 gap-x-4 gap-y-2 items-center hover:border-[#00C8FF]/40 transition-colors">
      <div className="col-span-2 lg:col-span-2 min-w-0">
        <div className="font-semibold text-sm truncate">{r.name || r.email}</div>
        <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>
      </div>
      <div className="text-xs"><div className="text-muted-foreground text-[10px] uppercase">Plan</div>{r.plan_label} · {r.term_months}m{r.auto_renew ? ' · auto' : ''}</div>
      <div className="text-xs"><div className="text-muted-foreground text-[10px] uppercase">Paid through</div><span data-testid={`sub-paid-through-${r.id}`}>{fmt(r.paid_through_date)}</span></div>
      <div className="text-xs"><div className="text-muted-foreground text-[10px] uppercase">Next programme</div><span data-testid={`sub-next-programme-${r.id}`}>{r.next_programme_date ? fmt(r.next_programme_date) : '—'}</span></div>
      <div className="text-xs"><div className="text-muted-foreground text-[10px] uppercase">Approval</div>
        <span data-testid={`sub-approval-${r.id}`} className={r.awaiting_approval ? 'text-[#FFB800] font-semibold' : ''}>
          {r.awaiting_approval ? `Needed · starts ${fmt(r.pending_start_date)}` : r.pending_approved ? `Approved · ${fmt(r.pending_start_date)}` : '—'}
        </span></div>
      <div className="lg:text-right"><StatusPill r={r} /></div>
    </Link>
  );
}

export default function Subscriptions() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  useEffect(() => { api.get('/admin/subscriptions').then(r => setData(r.data)).catch(() => setData({ summary: {}, rows: [] })); }, []);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.rows || []).filter(FILTERS.find(f => f.key === filter).test)
      .filter(r => !term || `${r.name || ''} ${r.email || ''}`.toLowerCase().includes(term));
  }, [data, filter, q]);
  const s = data?.summary || {};
  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#00C8FF]">Subscriptions</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-1">Subscription Overview</h1>
        <p className="text-sm text-muted-foreground mt-2">YourReGen+ and Protocol customers. Dates are Singapore time{s.today ? ` · today ${fmt(s.today)}` : ''}.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat icon={Users} label="Paid customers" value={s.total ?? '…'} color="#00C8FF" testid="sub-stat-total" />
        <Stat icon={CalendarClock} label="Active" value={s.active ?? '…'} color="#22C55E" testid="sub-stat-active" />
        <Stat icon={AlertTriangle} label="Expiring ≤ 7 days" value={s.expiring_soon ?? '…'} color="#FFB800" testid="sub-stat-expiring" />
        <Stat icon={ShieldCheck} label="Awaiting approval" value={s.awaiting_approval ?? '…'} color="#FF6B6B" testid="sub-stat-approval" />
        <Stat icon={XCircle} label="Ended" value={s.ended ?? '…'} color="#94A3B8" testid="sub-stat-ended" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email" data-testid="sub-search"
          className="h-9 px-3 rounded-full text-xs bg-transparent border border-[var(--c-hair)] w-full sm:w-64" />
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`sub-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === f.key ? 'border-[#00C8FF] bg-[#00C8FF]/10 text-[#00C8FF]' : 'border-[var(--c-hair)] text-muted-foreground'}`}>
            {f.label}
          </button>
        ))}
        {data && <span className="text-[11px] text-muted-foreground" data-testid="sub-count">{rows.length} shown</span>}
      </div>
      <div className="space-y-2" data-testid="sub-list">
        {!data && <div className="text-sm text-muted-foreground">Loading…</div>}
        {data && rows.length === 0 && <div className="regen-card p-6 text-sm text-muted-foreground text-center" data-testid="sub-empty">No subscriptions match this filter.</div>}
        {rows.map(r => <Row key={r.id} r={r} />)}
      </div>
    </div>
  );
}
